import { useCallback } from "react";
import { DevToolKey } from "@components/dev-features-dialog/features";
import {
  TogglesCallback,
  ButtonsCallback,
} from "@components/dev-features-dialog/features";
import { useAppDispatch } from "@store/store-helper";
import {
  setShouldShowAllPlans,
  setShouldShowAllProducts,
  setShouldShowFullDateTime,
  setShouldShowHardwareLicense,
  setIsSentryEnabled,
  setShouldAllowBulkOperations,
} from "@store/dev-features/dev-features-slice";
import { useFeatureToggle } from "@components/dev-features-dialog/use-feature-toggle";

/** Type definition for the return value of the useDevTools hook */
type UseDevToolsProps = Record<DevToolKey, ButtonsCallback | TogglesCallback>;

/**
 * Custom hook that provides development tool actions.
 */
export function useDevTools(): UseDevToolsProps {
  const dispatch = useAppDispatch();
  const { updateSearchParams } = useFeatureToggle();

  /** Allows showing all plans. */
  const showAllPlans = useCallback(
    (isEnabled: boolean) => {
      dispatch(setShouldShowAllPlans(isEnabled));
    },
    [dispatch]
  );

  /** Allows showing all products. */
  const showAllProducts = useCallback(
    (isEnabled: boolean) => {
      dispatch(setShouldShowAllProducts(isEnabled));
    },
    [dispatch]
  );

  /** Allows showing the full date and time. */
  const showFullDateTime = useCallback(
    (isEnabled: boolean) => {
      dispatch(setShouldShowFullDateTime(isEnabled));
    },
    [dispatch]
  );

  /** Allows showing the hardware license. */
  const showHardwareLicense = useCallback(
    (isEnabled: boolean) => {
      dispatch(setShouldShowHardwareLicense(isEnabled));
    },
    [dispatch]
  );

  /** Allows acting on several plans at once. */
  const allowBulkOperations = useCallback(
    (isEnabled: boolean) => {
      dispatch(setShouldAllowBulkOperations(isEnabled));
    },
    [dispatch]
  );

  /** Allows sending Sentry errors from numbered environments. */
  const enableSentry = useCallback(
    (isEnabled: boolean) => {
      dispatch(setIsSentryEnabled(isEnabled));

      updateSearchParams("enableSentry", isEnabled);
      globalThis.location.reload();
    },
    [dispatch, updateSearchParams]
  );

  return {
    showAllPlans,
    showAllProducts,
    showFullDateTime,
    showHardwareLicense,
    allowBulkOperations,
    enableSentry,
  };
}
